<template>
  <div class="welcome-panel">
    <!-- Background wave animations -->
    <div class="wave-background">
      <div class="wave wave1"></div>
      <div class="wave wave2"></div>
      <div class="wave wave3"></div>
    </div>

    <div class="speaker-panel">
      <!-- Modern heading with animation -->
      <div class="header-container">
        <h1 class="podcast-title">UnStruct.AI</h1>
        <p class="podcast-subtitle">Transforming information into conversations</p>
      </div>

      <!-- Your original message center and speakers remain unchanged -->
      <div class="message-center">
        <div
          v-for="speaker in speakers"
          :key="speaker.name"
          class="centered-message"
          :class="{ 'message-active': activeSpeaker === speaker.name }"
          :style="getMessageStyle(speakers.indexOf(speaker))"
        >
          {{ speaker.message }}
        </div>
      </div>

     <!-- Central play button and speakers grid -->
    <div class="central-play-container">
      <div class="play-instruction">
        <span class="play-prompt">Listen to our speakers</span>
      </div>
      <button
        class="play-button"
        :class="{ 'playing': isPlaying }"
        @click="togglePlayback"
      >
        <span class="play-icon"></span>
        <span class="pause-icon"></span>
      </button>
      <div class="play-instruction">
        <span class="play-description">Experience AI-powered conversations</span>
      </div>
    </div>

      <div class="speakers-container">
        <!-- All speakers in one row -->
        <div
          v-for="(speaker, index) in speakers"
          :key="index"
          class="speaker-card"
          :class="{ 'active': activeSpeaker === speaker.name }"
          @click="speakMessage(index)"
        >
          <div class="avatar-wrapper">
            <img
              :src="allImages[index]"
              :alt="`Speaker ${speaker.name} avatar`"
              class="avatar"
              :style="getAvatarStyle(speaker, index)"
            />
            <div
              v-if="activeSpeaker === speaker.name"
              class="pulse-rings"
              :class="{ 'rings-paused': !isPlaying }"
              :style="getPulseStyle(index)"
            >
              <div class="ring"></div>
              <div class="ring"></div>
            </div>
            <div
              v-if="activeSpeaker === speaker.name"
              class="speaking-indicator"
            >
              <div class="wave" :class="{ 'wave-paused': !isPlaying }">
                <div
                  v-for="n in 4"
                  :key="n"
                  class="wave-bar"
                  :style="getWaveBarStyle(index)"
                ></div>
              </div>
            </div>
          </div>
          <div class="speaker-info">
            <span class="speaker-name" :style="{ color: getTextColor(index) }">{{ speaker.name }}</span>
            <span class="speaker-role">{{ speaker.role }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Your original script remains unchanged
import API from "@/api";

export default {
  name: 'WelcomePanel',
  data() {
    return {
      activeSpeaker: null,
      isPlaying: false,
      allImages: [
        '/static/Gemini_Generated_Image_uobiifuobiifuobi.jpeg',
        '/static/Gemini_Generated_Image_b8negjb8negjb8ne.jpeg',
        '/static/Gemini_Generated_Image_yznx90yznx90yznx.jpeg',
        '/static/Gemini_Generated_Image_ns59fwns59fwns59.jpeg',
      ],
      speakers: [
        {
          name: "Sam Kokoro",
          role: "Host, UnStruct.AI",
          message: "UnStruct.AI rewires how information connects with human minds. Transform dense research papers into riveting discussions, complex data into accessible insights, or abstract concepts into tangible conversations that stick in memory long after listening stops."
        },
        {
          name: "Jordan Singh",
          role: "Research Scientist",
          message: "Revolutionize your professional brand by translating static LinkedIn profiles into dynamic narratives. Convert client testimonials or recommendations into immersive case studies. Turn quarterly reports into strategic dialogues. We're not just changing format - we're rewiring how information resonates."
        },
        {
          name: "Alex Chen",
          role: "Senior Analyst",
          message: "In a world drowning in content, breakthrough brands need neural resonance. Our platform transforms marketing copy into conversations that trigger precisely the right neurons. We don't just tell stories - we create experiences that bypass cognitive filters and establish lasting neural pathways."
        },
        {
          name: "Morgan Patel",
          role: "Lead Researcher",
          message: "Imagine your startup pitch evolving into a compelling dialogue between visionaries, your academic paper becoming an electrifying debate, or your product documentation transforming into interactive learning. UnStruct.AI bridges the gap between information delivery and neural integration."
        }
      ],
      colors: [
        { bg: '#FFEBEE', text: '#E53935' }, // Red
        { bg: '#F3E5F5', text: '#8E24AA' }, // Purple
        { bg: '#E8F5E9', text: '#43A047' }, // Green
        { bg: '#FFF3E0', text: '#FB8C00' }  // Orange
      ],
      // Audio-related properties
      autoCycleEnabled: false,
      cycleInterval: null,
      audioCache: new Map(), // Cache for audio segments.
      currentAudio: null,
      audioPlayer: null,
      backgroundMusic: null,
      backgroundMusicPlaying: false,
      voiceSpeed: 1.0,
      voiceProvider: 'local',
      speakerVoices: [
        "bf_emma+bf_v0isabella+af_heart+af_nicole",
        "af_alloy+af_aoede",
        "am_puck+am_onyx",
        "af_bella+af_alloy+af_jadzia+af_kore+af_sky",
      ],
      isLoading: false
    };
  },
  // Original methods and lifecycle hooks remain unchanged
  watch: {
    isPlaying(newVal) {
      this.$emit('welcome-panel-playing', newVal);
    },
  },
  created() {
    this.audioPlayer = new Audio();
    this.audioPlayer.addEventListener('ended', this.handleAudioEnded);
  },
  beforeDestroy() {
    this.stopSpeakerCycle();
    this.cleanupAudio();
  },
  methods: {
    togglePlayback() {
      if (this.isPlaying) {
        this.stopSpeakerCycle();
        if (this.backgroundMusic) {
          this.backgroundMusic.pause();
          this.backgroundMusicPlaying = false;
        }
        this.isPlaying = false;
      } else {
        if (!this.backgroundMusicPlaying) {
          this.playBackgroundMusic();
          this.backgroundMusicPlaying = true;
        }
        this.startSpeakerCycle();
        this.isPlaying = true;
      }
    },

    startSpeakerCycle() {
      this.speakMessage(0);
      this.autoCycleEnabled = true;
    },

    handleAudioEnded(speakerIndex) {
      if (this.autoCycleEnabled && speakerIndex < this.speakers.length - 1) {
          this.speakMessage(speakerIndex + 1);
      } else if (this.autoCycleEnabled && speakerIndex === this.speakers.length - 1) {
        this.stopSpeakerCycle();
        // emit event to parent component
        this.$emit('welcome-panel-complete');
        this.activeSpeaker = null;
      } else {
        setTimeout(() => {
          if (!this.isPlaying) {
            this.activeSpeaker = null;
          }
        }, 500);
      }
    },

    playBackgroundMusic() {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      const gainNode = audioContext.createGain();
      gainNode.connect(audioContext.destination);
      gainNode.gain.value = 0.05;
      this.backgroundMusic = new Audio(`https://${window.location.host}/static/Odyssey-Eveningland.mp3`);
      const source = audioContext.createMediaElementSource(this.backgroundMusic);
      source.connect(gainNode);
      if (this.isMobile) {
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) {
            this.backgroundMusic.pause();
          }
        });
      }
      this.backgroundMusic.play().catch(error => {
        console.error('Failed to play background music:', error);
      });
    },

    stopSpeakerCycle() {
      this.autoCycleEnabled = false;
      if (this.currentAudio && !this.currentAudio.paused) {
        this.currentAudio.pause();
      }
      if (this.backgroundMusic) {
        this.backgroundMusic.pause();
        this.backgroundMusicPlaying = false;
      }
      this.isPlaying = false;
    },

    async speakMessage(speakerIndex) {
      const speaker = this.speakers[speakerIndex];
      this.activeSpeaker = speaker.name;
      this.isPlaying = true;
      if (this.currentAudio && !this.currentAudio.paused) {
        this.currentAudio.pause();
      }
      try {
        if (this.audioCache.has(speakerIndex)) {
          this.playFromCache(speakerIndex);
          await this.preFetchNextSpeaker(speakerIndex);
        } else {
          await this.fetchAndPlayAudio(speakerIndex);
          await this.preFetchNextSpeaker(speakerIndex);
        }
      } catch (error) {
        console.error('Error playing speech:', error);
        this.isPlaying = false;
      }
    },

    async fetchAudioForSpeaker(speakerIndex) {
      const speaker = this.speakers[speakerIndex];
      const text = speaker.message;
      const voice = this.speakerVoices[speakerIndex];
      const token = localStorage.getItem("token");
      const unstruct_anonymous_key = localStorage.getItem("unstruct_anonymous_key");
      const url = token ? '/tts' : `/tts/anonymous/${unstruct_anonymous_key}`;
      const response = await API.post(url, {
        text,
        provider: this.voiceProvider,
        voice: voice,
        speed: this.voiceSpeed
      }, {
        responseType: 'arraybuffer',
        headers: {
          'Accept': 'audio/mpeg',
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    },

    async fetchAndPlayAudio(speakerIndex) {
      this.isLoading = true;
      try {
        const audioData = await this.fetchAudioForSpeaker(speakerIndex);
        this.audioCache.set(speakerIndex, audioData);
        this.playFromCache(speakerIndex);
      } catch (error) {
        console.error(`Failed to fetch audio for speaker ${this.speakers[speakerIndex].name}:`, error);
        this.isPlaying = false;
      } finally {
        this.isLoading = false;
      }
    },

    async preFetchNextSpeaker(currentIndex) {
      const nextIndex = currentIndex + 1;
      if (nextIndex < this.speakers.length && !this.audioCache.has(nextIndex)) {
        try {
          const audioData = await this.fetchAudioForSpeaker(nextIndex);
          this.audioCache.set(nextIndex, audioData);
        } catch (error) {
          console.warn(`Pre-fetch failed for speaker ${nextIndex}:`, error);
        }
      }
    },

    playFromCache(speakerIndex) {
      const audioData = this.audioCache.get(speakerIndex);
      const audioBlob = new Blob([audioData], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      if (this.currentAudio) {
        this.currentAudio.pause();
        if (this.currentAudio.src) {
          URL.revokeObjectURL(this.currentAudio.src);
        }
      }
      this.currentAudio = new Audio(audioUrl);
      this.currentAudio.addEventListener('ended', () => this.handleAudioEnded(speakerIndex));
      this.currentAudio.play();
    },

    cleanupAudio() {
      if (this.backgroundMusic) {
        this.backgroundMusic.pause();
        this.backgroundMusic = null;
      }
      if (this.currentAudio) {
        this.currentAudio.pause();
        if (this.currentAudio.src) {
          URL.revokeObjectURL(this.currentAudio.src);
        }
      }
      this.audioCache.forEach((_, key) => {
        this.audioCache.delete(key);
      });
      if (this.audioPlayer) {
        this.audioPlayer.removeEventListener('ended', this.handleAudioEnded);
      }
    },

    getAvatarStyle(speaker, index) {
      const color = this.colors[index % this.colors.length];
      return {
        boxShadow: `0 0 10px ${color.text}40`
      };
    },
    getPulseStyle(index) {
      const color = this.colors[index % this.colors.length];
      return {
        '--pulse-color': color.text
      };
    },
    getWaveBarStyle(index) {
      const color = this.colors[index % this.colors.length];
      return {
        backgroundColor: color.text
      };
    },
    getTextColor(index) {
      const color = this.colors[index % this.colors.length];
      return color.text;
    },
    getMessageStyle(index) {
      const color = this.colors[index % this.colors.length];
      return {
        backgroundColor: color.bg,
        color: color.text,
        border: `1px solid ${color.text}20`
      };
    }
  }
};
</script>

<style scoped>
/* Base styles from your original component */
.welcome-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: auto;
  position: relative;
  padding: 2rem 1rem;
  padding-bottom: 6rem; /* Added more bottom padding for chat input */
  gap: 0;
  overflow: hidden;
  color: white;
}

.theme--dark .welcome-panel {
  background: linear-gradient(135deg, #111827 0%, #1E293B 100%);
}

.theme-light .welcome-panel {
  background: linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%);
}

/* Modern Wave Background */
.wave-background {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  overflow: hidden;
  z-index: 0;
}

.wave {
  position: absolute;
  width: 200%;
  height: 200px;
  background-repeat: repeat-x;
  left: -50%;
}

.wave1 {
  bottom: -80px;
  background: url('data:image/svg+xml;utf8,<svg viewBox="0 0 1440 320" xmlns="http://www.w3.org/2000/svg"><path fill="%236366F1" fill-opacity="0.2" d="M0,160L48,144C96,128,192,96,288,90.7C384,85,480,107,576,133.3C672,160,768,192,864,197.3C960,203,1056,181,1152,165.3C1248,149,1344,139,1392,133.3L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path></svg>');
  animation: wave-animation 20s linear infinite;
  z-index: 1;
}

.wave2 {
  bottom: -60px;
  background: url('data:image/svg+xml;utf8,<svg viewBox="0 0 1440 320" xmlns="http://www.w3.org/2000/svg"><path fill="%234FECB8" fill-opacity="0.15" d="M0,288L48,272C96,256,192,224,288,213.3C384,203,480,213,576,229.3C672,245,768,267,864,261.3C960,256,1056,224,1152,213.3C1248,203,1344,213,1392,218.7L1440,224L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path></svg>');
  animation: wave-animation 15s linear infinite reverse;
  z-index: 2;
}

.wave3 {
  bottom: -30px;
  background: url('data:image/svg+xml;utf8,<svg viewBox="0 0 1440 320" xmlns="http://www.w3.org/2000/svg"><path fill="%23FFFFFF" fill-opacity="0.05" d="M0,128L40,122.7C80,117,160,107,240,122.7C320,139,400,181,480,208C560,235,640,245,720,234.7C800,224,880,192,960,176C1040,160,1120,160,1200,181.3C1280,203,1360,245,1400,266.7L1440,288L1440,320L1400,320C1360,320,1280,320,1200,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z"></path></svg>');
  animation: wave-animation 30s linear infinite;
  z-index: 3;
}

@keyframes wave-animation {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(50%);
  }
}

/* Modern speaker panel */
.speaker-panel {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Modern Header with Animation */
.header-container {
  text-align: center;
  position: relative;
  margin-bottom: 2.5rem;
  padding: 1rem;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  animation: fade-in-up 1s ease-out forwards;
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.podcast-title {
  margin-bottom: 0.5rem;
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(to right, #4FECB8, #6366F1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 5px 15px rgba(99, 102, 241, 0.3);
}

.podcast-subtitle {
  color: #94A3B8;
  margin-bottom: 0.5rem;
  font-size: 1.2rem;
  position: relative;
}

.podcast-subtitle::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  width: 60px;
  height: 3px;
  background: linear-gradient(to right, #4FECB8, #6366F1);
  transform: translateX(-50%);
  border-radius: 3px;
}

/* Original styles with some modern enhancements */
.message-center {
  position: relative;
  height: 80px;
  margin: 2rem 0 0 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 600px;
}

.centered-message {
  position: absolute;
  width: 100%;
  max-width: 600px;
  text-align: center;
  padding: 0.5rem;
  border-radius: 1rem;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
}

.message-active {
  opacity: 1;
  transform: translateY(0);
}

.speakers-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-top: 2rem;
  margin-bottom: 5rem;
  position: relative;
  width: 100%;
  max-width: 1200px;
}

.speaker-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 1rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(5px);
}

.speaker-card.active {
  transform: scale(1.05);
  background: rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.avatar-wrapper {
  position: relative;
  width: 80px;
  height: 80px;
}

.avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  transition: all 0.3s ease;
  border: 3px solid white;
}

.pulse-rings {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.ring {
  position: absolute;
  border-radius: 50%;
  border: 2px solid var(--pulse-color);
  opacity: 0;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.ring:nth-child(2) {
  animation-delay: 1s;
}

.speaking-indicator {
  position: absolute;
  bottom: -1.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
}

.wave {
  display: flex;
  align-items: center;
  gap: 2px;
  height: 12px;
}

.wave-bar {
  width: 2px;
  height: 100%;
  animation: wave 1s ease-in-out infinite;
  transform-origin: bottom;
}

.speaker-info {
  margin-top: 1.5rem;
  text-align: center;
}

.speaker-name {
  display: block;
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.speaker-role {
  display: block;
  font-size: 0.875rem;
  color: #94A3B8;
}

.central-play-container {
  position: static;
  margin: 2rem auto;
  transform: none;
  display: flex;
  justify-content: center;
  z-index: 10;
}

.play-button {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.theme--dark .play-button {
  background: #2D3748;
  border-color: #4A5568;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
}

.theme-light .play-button {
  background: #2D3748;
  border-color: #CBD5E0;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.play-button:hover {
  transform: scale(1.05);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.4), 0 0 20px rgba(99, 102, 241, 0.3);
}

.play-button:active {
  transform: scale(0.95);
}

.play-icon,
.pause-icon {
  position: absolute;
  transition: all 0.3s ease;
}

.play-icon {
  border-style: solid;
  border-width: 15px 0 15px 25px;
  border-color: transparent transparent transparent #FFFFFF;
  margin-left: 5px;
  opacity: 1;
}

.pause-icon {
  width: 8px;
  height: 25px;
  background: #FFFFFF;
  margin-right: 13px;
  opacity: 0;
  box-shadow: 15px 0 0 #FFFFFF;
}

.play-button.playing .play-icon {
  opacity: 0;
}

.play-button.playing .pause-icon {
  opacity: 1;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.5;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

@keyframes wave {
  0%, 100% { transform: scaleY(0.5); }
  50% { transform: scaleY(1); }
}

/* Responsive Adjustments */
@media (max-width: 1024px) {
  .speakers-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  .play-button {
    width: 60px;
    height: 60px;
  }

  .avatar-wrapper {
    width: 100px;
    height: 100px;
  }
}

@media (max-width: 768px) {
  .podcast-title {
    font-size: 2rem;
  }

  .message-center {
    height: 120px;
  }

  .centered-message {
    max-width: 90%;
    font-size: 0.9rem;
  }

  .speakers-container {
    grid-template-columns: repeat(2, 1fr); /* Ensure 2x2 grid on mobile */
    margin-bottom: 5rem;
    gap: 1.5rem;
  }

  .avatar-wrapper {
    width: 80px;
    height: 80px;
  }

  .welcome-panel {
    padding: 1rem;
    padding-bottom: 10rem; /* Increased bottom padding for chat input */
    margin-bottom: 2rem; /* Additional margin at bottom */
  }

  .play-button {
    width: 50px;
    height: 50px;
  }

  .play-icon {
    border-width: 12px 0 12px 20px;
  }

  .pause-icon {
    width: 6px;
    height: 20px;
    box-shadow: 12px 0 0 #FFFFFF;
  }
}

/* Remove the single column on very small screens to maintain 2x2 grid */
@media (max-width: 480px) {
  .speakers-container {
    grid-template-columns: repeat(2, 1fr); /* Keep 2x2 grid even on smallest screens */
    gap: 1rem;
  }

  .avatar-wrapper {
    width: 70px;
    height: 70px;
  }

  .welcome-panel {
    padding-bottom: 12rem; /* Even more padding for smallest screens */
  }
}


.play-instruction {
  text-align: center;
  margin: 0 1rem;
  color: #94A3B8;
}

.play-prompt {
  font-weight: 500;
  color: #6366F1;
  font-size: 1rem;
}

.play-description {
  font-size: 0.9rem;
}

.central-play-container {
  position: static;
  margin: 2rem auto;
  transform: none;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .central-play-container {
    flex-direction: column;
    gap: 0.5rem;
  }

  .play-instruction {
    margin: 0.5rem 0;
  }
}
</style>
